import { useEffect, useState } from 'react';
import { Button, InputPlain } from '@peacecover/component';
import { ArrowLeft, ArrowRightSmall } from '@peacecover/icon';
import { t } from 'i18next';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import { getJosa } from '../../../utils/bettieUtils';
import AnimationStarBeam from './AnimationStarBeam';

type PersonaInputStepSlot = 'first' | 'second';

type PersonaInputStepProps = {
  slot: PersonaInputStepSlot;
  onBack: () => void;
  onSetAsMe: () => void;
  nickname: string;
  onNicknameChange: (value: string) => void;
  onNext: () => void;
  /** 두 번째 슬롯일 때, 첫 인물이 User면 '나로 설정' 버튼 숨김 */
  isFirstPersonaUser?: boolean;
  /** 두 번째 슬롯일 때, 첫 번째 인물 닉네임 (첫 번째 문장에 표시) */
  firstPersonaNickname?: string;
};

const TITLE_BY_SLOT: Record<PersonaInputStepSlot, string> = {
  first: t('SceneSetting.personaInput.firstTitle'),
  second: t('SceneSetting.personaInput.secondTitle')
};

const SET_AS_ME_LABEL_BY_SLOT: Record<PersonaInputStepSlot, string> = {
  first: t('SceneSetting.personaInput.setAsMeFirst'),
  second: t('SceneSetting.personaInput.setAsMeSecond')
};

const PersonaInputStep = ({
  slot,
  onBack,
  onSetAsMe,
  nickname,
  onNicknameChange,
  onNext,
  isFirstPersonaUser = false,
  firstPersonaNickname = ''
}: PersonaInputStepProps) => {
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  // 입력 도중 조사 깜빡임을 줄이기 위해, 일정 시간 입력이 멈춘 뒤에만 조사 계산
  const [settledNickname, setSettledNickname] = useState(nickname);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSettledNickname(nickname);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [nickname]);

  const firstLineNicknameForJosa = slot === 'first' ? settledNickname : firstPersonaNickname;
  const isSameNickname = slot === 'second' && firstPersonaNickname === nickname;

  const titleLabel = TITLE_BY_SLOT[slot];
  const setAsMeLabel = SET_AS_ME_LABEL_BY_SLOT[slot];
  const showSetAsMeButton = slot === 'first' || !isFirstPersonaUser;
  const isNextDisabled = !nickname.trim() || isSameNickname;

  return (
    <div className="bg-background-dark min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] overflow-x-hidden relative">
      <header className="px-[16px] py-[15px] z-[1] relative">
        <button type="button" onClick={onBack}>
          <ArrowLeft size={24} className="text-pure-white" />
        </button>
      </header>
      <div
        className="rounded-full w-[1020px] h-[1020px] absolute top-[-798px] left-1/2 -translate-x-1/2 flex flex-col justify-end items-center"
        style={{
          background:
            'radial-gradient(50% 50% at 50% 50%, #0D0E12 23.08%, #0D0E12 73.08%, #130757 92.31%, #19009C 100%)'
        }}
      >
        <AnimationStarBeam />
        <div
          className="text-pure-white mb-[50px] flex flex-col items-center gap-[1px]"
          style={{
            fontFamily: '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif'
          }}
        >
          <div className="flex items-center gap-[3px]">
            {slot === 'first' ? (
              <div className="min-w-[39px] h-[19px] text-center text-[16px] flex items-center justify-center leading-[23px] text-pure-white rounded-[6px] bg-[rgba(0,0,0,0.14)] shadow-[0_0_12px_0_#FFF] px-[2px]">
                {nickname || ''}
              </div>
            ) : (
              <span className="text-[16px] leading-[23px]">
                {firstPersonaNickname === '나' ? '내' : firstPersonaNickname || ''}
              </span>
            )}
            <span className="text-[16px] leading-[23px]">
              {getJosa(firstLineNicknameForJosa, '이/가') || '가'} 먼저 움직였고,
            </span>
          </div>
          <div className="flex items-center">
            {slot === 'first' ? (
              <div className="text-[16px] leading-[23px]" style={{ filter: 'blur(5.5px)' }}>
                쇼타로
              </div>
            ) : (
              <div className="min-w-[39px] h-[19px] text-center text-[16px] flex items-center justify-center leading-[23px] text-pure-white rounded-[6px] bg-[rgba(0,0,0,0.14)] shadow-[0_0_12px_0_#FFF] px-[2px]">
                {nickname || ''}
              </div>
            )}
            <span className="text-[16px] leading-[23px]">
              {getJosa(slot === 'first' ? '쇼타로' : settledNickname, '은/는') || '는'} 거기에 맞춰
              움직였다.
            </span>
          </div>
        </div>
      </div>
      <div className="mt-[199px]">
        <h1 className="px-[24px] py-[10px] text-[26px] font-semibold leading-[31px] text-pure-white text-center">
          {t('SceneSetting.personaInput.storyAppear')}
          <br />
          {titleLabel}
        </h1>
        {showSetAsMeButton ? (
          <div className="flex justify-end mt-[18px] px-[24px]">
            <button
              type="button"
              className="bg-[linear-gradient(135deg,#15CCCB_-14.28%,#4987FF_42.83%,#8B1CFA_101.7%)] rounded-[30px] py-[4px] pl-[8px] pr-[4px] flex items-center gap-[2px]"
              onClick={onSetAsMe}
            >
              <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
                {setAsMeLabel}
              </p>
              <ArrowRightSmall size={13} className="text-pure-white" />
            </button>
          </div>
        ) : (
          <div className="h-[21px] mt-[18px]" />
        )}
        <div className="px-[16px] py-[17px] mt-[10px] bg-secondary-dark rounded-[12px] mx-[24px]">
          <InputPlain
            placeholder={t('SceneSetting.personaInput.placeholder') as string}
            className="bg-transparent placeholder:text-placeholder-dark !text-main-dark-2"
            value={nickname}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => onNicknameChange(e.target.value)}
          />
        </div>
        {isSameNickname && (
          <p className="mt-[8px] text-[13px] font-medium leading-[16px] text-[#FF495A] mx-[24px]">
            {t('SceneSetting.personaInput.warningText')}
          </p>
        )}
        <ul className="text-[12px] text-main-dark-2 leading-[14px] mt-[16px] ml-[33px] flex flex-col items-start gap-[4px] list-disc list-inside">
          <li>{t('SceneSetting.personaInput.firstInstruction')}</li>
          <li>{t('SceneSetting.personaInput.secondInstruction')}</li>
        </ul>
      </div>
      <div
        className={`fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+17px)] transition-all duration-150 ease-in-out ${
          isKeyboardVisible
            ? 'translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={isKeyboardVisible}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold disabled:bg-[#303030] disabled:text-[#4D4D50]"
          disabled={isNextDisabled}
          onClick={onNext}
        >
          {t('next')}
        </Button>
      </div>
    </div>
  );
};

export default PersonaInputStep;
